<template>
    <div>
        <v-dialog :value="value" @input="$emit('input', $event)" max-width="900" scrollable persistent>
        <v-card class="oculis-filter-hub overflow-hidden">
            <!-- Add a loading bar across the top of the dialog while fetching filter options/saved filters -->
            <v-progress-linear 
                v-if="filterLoading" 
                indeterminate 
                color="primary" 
                height="4" 
                class="position-absolute" 
                style="top: 0; left: 0; z-index: 10;"
            ></v-progress-linear>

            <v-card-title class="grey lighten-4 py-4 px-6 border-bottom">
            <v-icon color="primary" class="mr-3">{{ icons.mdiFilterVariant }}</v-icon>
            <span class="mr-4 font-weight-bold">Decipher Filters</span>
            <v-spacer></v-spacer>
            <v-btn icon @click="$emit('input', false)" :disabled="filterLoading">
                <v-icon>{{ icons.mdiClose }}</v-icon>
            </v-btn>
            </v-card-title>

            <v-card-text class="pa-0">
            <v-expansion-panels v-model="openPanel" accordion flat tile class="filter-panels elevation-0">
                <v-expansion-panel :disabled="activeSource === 'quota' || filterLoading" class="border-bottom no-shadow">
                <v-expansion-panel-header class="py-5 px-6 d-flex justify-start align-center">
                    <template v-slot:default>
                    <div class="d-flex align-center header-content">
                        <v-icon :color="activeSource === 'quota' ? 'grey lighten-2' : 'primary'" class="mr-4">{{ icons.mdiTableLarge }}</v-icon>
                        <span class="text-h7 font-weight-bold">1. Crosstabs</span>
                        <v-chip v-if="selectedCrosstabs.length && activeSource === 'crosstab'" x-small color="primary" class="ml-4" outlined>
                        {{ selectedCrosstabs.length }} selected
                        </v-chip>
                    </div>
                    </template>
                </v-expansion-panel-header>
                <v-expansion-panel-content class="px-6">
                    <div class="filter-scroll-container">
                    <div v-for="group in allCrosstabs" :key="group.id" 
                        :class="{'is-disabled': activeHeadline && activeHeadline !== group.title}"
                        class="mb-6 filter-group"
                    >
                        <div class="text-overline primary--text font-weight-black mb-1">{{ group.title }}</div>
                        <v-divider class="mb-3"></v-divider>
                        <v-row dense>
                        <v-col v-for="seg in group.segments" :key="seg.id" cols="12" sm="4" md="3">
                            <v-checkbox 
                            v-model="selectedSegments" 
                            :label="seg.name" 
                            :value="seg.id" 
                            dense hide-details class="mt-0 custom-checkbox" 
                            @change="selectedCrosstabs = selectedSegments.length ? [group.title] : []"
                            />                     
                        </v-col>
                        </v-row>
                    </div>
                    </div>
                </v-expansion-panel-content>
                </v-expansion-panel>

                <v-expansion-panel :disabled="activeSource === 'crosstab' || filterLoading" class="no-shadow">
                <v-expansion-panel-header class="py-5 px-6 d-flex justify-start align-center">
                    <template v-slot:default>
                    <div class="d-flex align-center header-content">
                        <v-icon :color="activeSource === 'crosstab' ? 'grey lighten-2' : 'primary'" class="mr-4">{{ icons.mdiShieldCheck }}</v-icon>
                        <span class="text-h7 font-weight-bold">2. Quotas</span>
                        <v-chip v-if="selectedQuotas.length && activeSource === 'quota'" x-small color="primary" class="ml-4" outlined>
                        {{ selectedQuotas.length }} selected
                        </v-chip>
                    </div>
                    </template>
                </v-expansion-panel-header>
                <v-expansion-panel-content class="px-6">
                    <div class="filter-scroll-container">
                    <div v-for="group in allQuotas" :key="group.id" class="mb-6 filter-group">
                        <div class="text-overline success--text font-weight-black mb-1">{{ group.title }}</div>
                        <v-divider class="mb-3"></v-divider>
                        <v-row dense>
                        <v-col v-for="seg in group.segments" :key="seg.id" cols="12" sm="4" md="3">
                        <v-checkbox v-model="selectedSegments" :label="seg.name" :value="seg.id" dense hide-details class="mt-0 custom-checkbox" /> </v-col>
                        </v-row>
                    </div>
                    </div>
                </v-expansion-panel-content>
                </v-expansion-panel>
            </v-expansion-panels>
            </v-card-text>

            <v-divider></v-divider>

            <v-card-actions class="pa-4 white d-flex align-center">
            <v-combobox
                v-model="selectedSavedFilter"
                :items="savedFilters"
                item-text="name"
                placeholder="Search or type name to save..."
                outlined dense hide-details
                style="max-width: 400px;"
                class="mr-3 filter-selector"
                clearable
                :disabled="filterLoading"
                @change="handleApplySaved"
            >
                <template v-slot:selection="{ item }">
                <v-chip small color="primary" outlined v-if="typeof item === 'string'">
                    <v-icon left x-small>{{ icons.mdiPlus }}</v-icon> NEW: {{ item }}
                </v-chip>
                <span v-else class="text-caption font-weight-bold">{{ item.name }}</span>
                </template>
                <template v-slot:item="{ item }">
                <v-list-item-content>
                    <v-list-item-title class="text-caption">{{ item.name }}</v-list-item-title>
                </v-list-item-content>
                <v-list-item-action class="flex-row ma-0">
                    <v-btn icon x-small @click.stop="openManagementDialog('rename', item)"><v-icon x-small>{{ icons.mdiPencil }}</v-icon></v-btn>
                    <v-btn icon x-small @click.stop="openManagementDialog('delete', item)"><v-icon x-small color="error">{{ icons.mdiDelete }}</v-icon></v-btn>
                </v-list-item-action>
                </template>
                <template v-slot:no-data>
                <v-list-item>
                    <v-list-item-content>
                    <v-list-item-title class="text-caption">Press <kbd>enter</kbd> to name this filter.</v-list-item-title>
                    </v-list-item-content>
                </v-list-item>
                </template>
            </v-combobox>

            <v-btn outlined color="primary" @click="saveCurrentFilter" :disabled="!selectedSavedFilter || filterLoading" height="40">
                <v-icon left small>{{ icons.mdiContentSaveOutline }}</v-icon> Save
            </v-btn>
            <v-btn text color="grey darken-1" small class="ml-2" @click="clearFilters" :disabled="filterLoading">Reset</v-btn>
            <v-spacer></v-spacer>
            <v-btn color="success" depressed @click="handleApplyFilters" :disabled="filterLoading" :loading="analysisLoading" min-width="180" height="44">
                <v-icon left>{{ icons.mdiPlay }}</v-icon> Apply Filters
            </v-btn>
            </v-card-actions>
        </v-card>
        </v-dialog>

        <v-dialog v-model="mgmtDialog.show" max-width="400">
        <v-card v-if="mgmtDialog.item">
            <v-card-title class="headline grey lighten-4 py-3">{{ mgmtDialog.mode === 'rename' ? 'Rename Filter' : 'Delete Filter' }}</v-card-title>
            <v-card-text class="pt-6">
            <template v-if="mgmtDialog.mode === 'rename'">
                <v-text-field v-model="mgmtDialog.tempName" label="New Filter Name" outlined dense autofocus @keyup.enter="confirmManagementAction" />
            </template>
            <template v-else>
                <p class="text-body-1">Are you sure you want to delete <strong>{{ mgmtDialog.item.name }}</strong>?</p>
                <p class="text-caption red--text">This action cannot be undone.</p>
            </template>
            </v-card-text>
            <v-card-actions class="pb-4 px-4">
            <v-spacer></v-spacer>
            <v-btn text @click="mgmtDialog.show = false">Cancel</v-btn>
            <v-btn :color="mgmtDialog.mode === 'rename' ? 'primary' : 'error'" depressed @click="confirmManagementAction">Confirm</v-btn>
            </v-card-actions>
        </v-card>
        </v-dialog>
        

        
        <v-snackbar v-model="notification.show" :color="notification.color" :timeout="4000" elevation="24">
        {{ notification.text }}
        <template v-slot:action="{ attrs }">
            <v-btn text v-bind="attrs" @click="notification.show = false">Close</v-btn>
        </template>
        </v-snackbar>
    </div>
</template>

<script>
import { ref, watch, computed, onMounted } from '@vue/composition-api'
import useOculis from '../../useOculis'
import {  mdiContentSave, 
  mdiTableLarge, mdiAccountGroup, mdiDelete, mdiPlay, mdiFilterVariant, mdiPencil,
  mdiContentSaveOutline, mdiRefresh, mdiShieldCheck, mdiClose, mdiPlus, mdiImagePlus
} from '@mdi/js'

export default {
    props: {
        value: {type: Boolean},
        project: { type: Object, default: () => ({}) },
    },
    setup(props, { emit }) {
        const filterDialog = ref(false)
        const openPanel = ref(2) 
        const selectedCrosstabs = ref([])
        const selectedQuotas = ref([])
        const selectedSegments = ref([]) 
        const selectedSavedFilter = ref(null)
        
        // Filter Dialog State
        const selectedCrosstab = ref(null)
        const selectedBanners = ref({})

        const mgmtDialog = ref({
            show: false,
            mode: 'rename',
            item: null,
            tempName: ''
        })

        const notification = ref({
            show: false,
            text: '',
            color: 'error'
        })

        const { 
            filterOptions, allCrosstabs, allQuotas, savedFilters, activeFilters,
            fetchSavedFilters, saveFilter, deleteFilter, renameFilter,
            fetchFilterOptions, applyAnalysis, analysisLoading, filterLoading, selectedItem,  
        } = useOculis()
        
        watch(filterOptions, (newVal) => {
            if(newVal && newVal.length > 0) {
                const initBanners = {}
                newVal.forEach(ct => {
                initBanners[ct.id] = selectedBanners.value[ct.id] || []
                })
                selectedBanners.value = { ...selectedBanners.value, ...initBanners }
            }
        }, { immediate: true })

        const extractLogic = (ids) => {
            if (!ids || ids.length === 0) return []

            // Combine all groups from both crosstabs and quotas
            const allGroups = [
                ...allCrosstabs.value,
                ...allQuotas.value
            ]

            const groupedConditions = []

            // Iterate through each group to construct the intra-group OR logic
            allGroups.forEach(group => {
                // Find all selected segments that belong to the current group
                const selectedInGroup = group.segments.filter(seg => ids.includes(seg.id))
                
                if (selectedInGroup.length > 0) {
                    // Extract valid logic strings
                    const logics = selectedInGroup.map(seg => seg.logic).filter(l => l)
                    
                    if (logics.length > 0) {
                        // Apply OR logic for segments within the SAME group
                        // Wrap each segment logic in parentheses to prevent syntax bleeding
                        const orStatement = logics.map(l => `(${l})`).join(' or ')
                        
                        // Push the group's bundled OR logic into our final array
                        groupedConditions.push(`(${orStatement})`)
                    }
                }
            })

            // Returns an array of formatted group strings: e.g. ["((A) or (B))", "((C))"]
            return groupedConditions 
        }

        const activeSource = computed(() => {
            if (selectedSegments.value.length === 0) return null
            const all = [...allCrosstabs.value, ...allQuotas.value]
            for (const group of all) {
                if (group.segments.some(s => s.id === selectedSegments.value[0])) return group.type
            }
            return null
        })

        const activeHeadline = computed(() => {
            if (activeSource.value !== 'crosstab' || selectedSegments.value.length === 0) return null
            for (const group of allCrosstabs.value) {
                if (group.segments.some(s => s.id === selectedSegments.value[0])) return group.title
            }
            return null
        })

        const handleApplyFilters = async () => {
            // Check if segments are selected or a saved filter is loaded
            const hasSelectedSegments = selectedSegments.value.length > 0
            const hasSavedFilter = selectedSavedFilter.value &&
                typeof selectedSavedFilter.value === 'object' &&
                selectedSavedFilter.value.id

            if (!hasSelectedSegments && !hasSavedFilter) {
                notification.value = {
                    show: true,
                    text: "Please select or load a filter before applying.",
                    color: "warning"
                }
                return
            }
            
            
            emit('filter-applied', selectedSegments.value.length)
            await applyAnalysis(props.project.id, selectedItem.value?.id, {
                conditions: extractLogic(selectedSegments.value)
            })
            emit('input', false)
        }

        const handleApplySaved = (filterObj) => {
            if (!filterObj || typeof filterObj === 'string') return
            const state = filterObj.conds || {}
            selectedSegments.value = state.selectedSegments || []
            selectedCrosstabs.value = state.selectedCrosstabs || []
            selectedQuotas.value = state.selectedQuotas || []
            activeFilters.value = { 
                conditions: extractLogic(selectedSegments.value) 
            }

            notification.value = { 
              show: true,
              text: "Saved filter loaded. Click 'Apply' to see results.",
              color: "info" 
            }
        }

        const saveCurrentFilter = async () => {
            if (selectedSegments.value.length === 0) {
                notification.value = {
                    show: true,
                    text: "You must check at least one filter from Crosstabs or Quotas before saving.",
                    color: "warning"
                }
                return
            }

            const filterName = typeof selectedSavedFilter.value === 'string' 
                ? selectedSavedFilter.value 
                : selectedSavedFilter.value?.name

            if (!filterName) return 

            const payload = {
                name: filterName,
                conditions: extractLogic(selectedSegments.value),
                conds: {
                  selectedSegments: selectedSegments.value,
                  selectedCrosstabs: selectedCrosstabs.value,
                  selectedQuotas: selectedQuotas.value 
                }
            }

            try {
                await saveFilter(props.project.id, payload)
                await fetchSavedFilters(props.project.id)
                selectedSavedFilter.value = null

                notification.value = {
                    show: true,
                    text: "Filter saved successfully!",
                    color: "success"
                }
            } catch (error) {
                console.error("❌ Failed to save filter:", error)
            }
        }

        const clearFilters = async () => {
            selectedCrosstabs.value = []
            selectedQuotas.value = []
            selectedSegments.value = []
            selectedSavedFilter.value = null
            await applyAnalysis(props.project.id, selectedItem.value?.id, { conditions: [] })
            emit('filter-applied', selectedSegments.value.length)
            emit('input', false)
            notification.value = { show: true, text: "Filters cleared.", color: "info" }
        }

        const openManagementDialog = (mode, item) => {
            mgmtDialog.value = { show: true, mode: mode, item: item, tempName: item.name }
        }

        const confirmManagementAction = async () => {
            const { mode, item, tempName } = mgmtDialog.value
            if (mode === 'rename') {
                await renameFilter(props.project.id, item.id, tempName)
                notification.value = { show: true, text: "Filter renamed!", color: "success" }
            } else {
                await deleteFilter(props.project.id, item.id)
                if (selectedSavedFilter.value?.id === item.id) selectedSavedFilter.value = null
                notification.value = { show: true, text: "Filter deleted", color: "info" }
            }
            mgmtDialog.value.show = false
        }

        watch(
            () => props.project.id,
            (id) => {
                activeFilters.value = { 
                    conditions: [],   
                    filter_id: null   
                }
                if (id) {
                    if (filterOptions.value && filterOptions.value.length > 0) {
                        selectedCrosstab.value = filterOptions.value[0]
                    }
                    fetchFilterOptions(id);
                    fetchSavedFilters(id);
                } 
        })

        return {
            filterDialog, openPanel, selectedCrosstabs, selectedQuotas, selectedSegments, selectedSavedFilter,
            allCrosstabs, allQuotas, analysisLoading, filterLoading,
            handleApplyFilters, handleApplySaved, saveCurrentFilter, clearFilters,
            openManagementDialog, confirmManagementAction, mgmtDialog, notification,
            activeSource, activeHeadline, savedFilters, 
            
            icons: { 
                mdiAccountGroup, mdiDelete, mdiPlay, mdiFilterVariant, mdiTableLarge,mdiContentSave,
                mdiContentSaveOutline, mdiRefresh, mdiShieldCheck, mdiClose, mdiPlus, mdiImagePlus, mdiPencil
            }
        }
    }
}
</script>